import { KeyRound, LogOut, UserRound } from 'lucide-react';
import { useState } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChangePasswordDialog, ProfileDialog, useAuth, useLogout } from '@/features/auth';
import { roleLabel } from '@/shared/auth/permissions';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** Identity from `/admin/me`: name, username and a friendly role label. */
export function UserMenu() {
  const { user } = useAuth();
  const logout = useLogout();
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  if (!user) return null;

  const displayName = user.name || user.userName;
  const label = roleLabel(user.role);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 gap-2 px-1.5" aria-label="Account menu">
            <Avatar className="size-6">
              <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">
                {initialsOf(displayName)}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-xs font-medium sm:inline">{displayName}</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuLabel className="font-normal">
            <p className="truncate text-xs font-semibold">{displayName}</p>
            <p className="text-muted-foreground mt-0.5 truncate text-[11px]">
              {user.userName} · {label}
            </p>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => setEditingProfile(true)} className="gap-2 text-xs">
            <UserRound className="size-3.5" aria-hidden />
            Profile
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={() => setChangingPassword(true)} className="gap-2 text-xs">
            <KeyRound className="size-3.5" aria-hidden />
            Change password
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={logout} className="gap-2 text-xs" variant="destructive">
            <LogOut className="size-3.5" aria-hidden />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
      {editingProfile && (
        <ProfileDialog name={user.name ?? ''} onClose={() => setEditingProfile(false)} />
      )}
    </>
  );
}
